import type { GongFlowStepDetailWire } from '@shared/flow/gong-flow-catalog';

/**
 * The three step-type primitives every step-override surface reads: which
 * types carry an email body, whether one step has a rep-editable Subject at
 * all, and the plain-English copy for a step type.
 *
 * They lived in `StepOverrideEditor.tsx` until plan 2026-09-30-002 U3 split
 * that component into a model hook (`useStepOverrideRowModel.ts`) and a view,
 * which put the hook and the component on both ends of an import cycle
 * (`import/no-cycle`, `.oxlintrc.json`). This leaf is the "extract shared code
 * into a separate module both files can import" fix. **The definitions below
 * are byte-identical to the ones that stood in `StepOverrideEditor.tsx`** —
 * nothing was retyped or re-derived, and `StepOverrideEditor.tsx` re-exports
 * all three, so every existing import path (`EnrollQueueContext.tsx`'s
 * `EMAIL_STEP_TYPES`, and the V2 rail's `GONG_STEP_TYPE_LABELS`) is unchanged.
 */

/**
 * Gong step types whose default copy includes a rep-editable Subject line —
 * every other type's Subject is Gong's dialer/LinkedIn/CUSTOM shape, which
 * has no separate subject field. Exported so the network-boundary code that
 * turns a `StepOverrideSnapshot` into a PUT patch (`EnrollQueueContext.saveStepOverrides`)
 * can drop `subject_draft` for non-email steps — see that call site's doc
 * comment for why (Codex #4, round-5 adversarial follow-up).
 */
export const EMAIL_STEP_TYPES: ReadonlySet<GongFlowStepDetailWire['step_type']> = new Set(['SEND_EMAIL', 'AUTO_SEND_EMAIL']);

/**
 * Whether this step has a rep-editable Subject line at all.
 *
 * Two independent reasons it may not, and BOTH have to hold for the field to
 * render:
 *  - the step type has no subject concept (dialer / LinkedIn / CUSTOM), and
 *  - the step is a Gong REPLY step, which threads under the first step's
 *    subject. Gong REJECTS an override that sets a subject on one
 *    (`Step subject cannot be set for reply steps`) and 400s the ENTIRE
 *    enrollment, not just the offending step — so an email-family step can
 *    still be subject-less.
 *
 * Exported as a predicate rather than leaving callers to re-derive
 * `EMAIL_STEP_TYPES.has(...) && !is_reply`: the rule now has two clauses, and
 * two call sites disagreeing about it is exactly how the reply-step 400
 * reaches a prospect-facing send.
 */
export function stepHasSubjectField(step: GongFlowStepDetailWire): boolean {
  return EMAIL_STEP_TYPES.has(step.step_type) && !step.is_reply;
}

/**
 * Plain-English step-header copy — never the raw Gong enum
 * (`step.step_type`) directly. A `Record` over the closed
 * `GongFlowStepActionTypeSchema` enum type (not a partial lookup with a
 * fallback) so that a future 7th Gong step type is a COMPILE ERROR here,
 * not a silently-blank header — the same pattern as `SEND_SKIP_REASON_LABELS`
 * (`itemBlockReason.ts:38`).
 *
 * `SEND_EMAIL` ("Manual Email") and `AUTO_SEND_EMAIL` ("Auto Email") MUST
 * stay distinct labels — do not collapse them into one "Email" entry. The
 * schema-level deny-list rationale (`gong-flow.ts:10-16`) is that Gong's
 * automation-flow detection reads off step composition, which depends on
 * `AUTO_SEND_EMAIL` remaining a distinguishable action type; collapsing the
 * copy here would erode the same signal for the rep reading the header.
 *
 * Two render sites now, both going through `useStepOverrideRowModel`'s
 * `stepTypeLabel`: the V1 `StepOverrideRow` and the V2 rail's `StepRow`
 * (plan 2026-09-30-002 D28). Still the only place `step_type` becomes copy.
 */
export const GONG_STEP_TYPE_LABELS: Record<GongFlowStepDetailWire['step_type'], string> = {
  SEND_EMAIL: 'Manual Email',
  AUTO_SEND_EMAIL: 'Auto Email',
  CALL: 'Call',
  LINKEDIN_CONNECT: 'LinkedIn Connect',
  LINKEDIN_MESSAGE: 'LinkedIn Message',
  CUSTOM: 'Custom Step',
};
